import React, { ComponentProps, Children } from 'react';
import { cloneElement } from 'react';
import { useMemo } from 'react';
import { prepareProps } from '../../utils/propUtils';
import { classes } from '../../utils/utils';
import styles from './CardListItem.module.css';
import {
  CardListItemInfo,
  WrappedCardListItemSubComponentElement
} from './cardListItemSubComponent';

export enum CardListItemSize {
  Normal = 'normal',
  Small = 'small'
}

export type CardListItemProps = Omit<ComponentProps<'div'>, 'ref'> & {
  /**
   * default Normal
   */
  size?: CardListItemSize;
  children?:
    | Array<WrappedCardListItemSubComponentElement | null | undefined>
    | WrappedCardListItemSubComponentElement;
  /**
   * default: false
   */
  inline?: boolean;
};

/**
 * these classes set a --card-list-item-height css var so you can style your components appropiately
 */
export const cardListItemSizeToHeightClassName: Record<
  CardListItemSize,
  string
> = {
  normal: styles.cardListItemHeightNormal,
  small: styles.cardListItemHeightSmall
};

// eslint-disable-next-line react/no-multi-comp
export const CardListItem = React.memo(
  React.forwardRef<HTMLDivElement, CardListItemProps>((props, ref) => {
    const {
      children = [],
      className,
      size = CardListItemSize.Normal,
      inline = false,
      ...remainingProps
    } = prepareProps({ props });

    const cardListItemInfo: CardListItemInfo = useMemo(
      () => ({ size }),
      [size]
    );
    return (
      <div
        {...remainingProps}
        className={classes(
          className,
          styles.wrapper,
          cardListItemSizeToHeightClassName[size],
          inline ? styles.wrapperInline : null
        )}
        ref={ref}
      >
        {Children.map<
          unknown,
          | React.ReactElement<{ cardListItemInfo: CardListItemInfo }>
          | null
          | undefined
        >(children, (c, index) => {
          // only pass values to custom elements
          if (c == null || typeof c.type === 'string') {
            return c;
          }

          return cloneElement(c, {
            cardListItemInfo,
            key: c.key ? c.key : `card-list-item-child-index-${index}`
          });
        })}
      </div>
    );
  })
);
